/**
 * useCustomLights Hook
 * 
 * Custom lights management:
 * - Add lights (Point, Spot, Directional, Area)
 * - Edit properties (position, color, intensity, shadows)
 * - Delete lights
 * - Helpers visibility
 * 
 * @param {Object} sceneRef - Scene reference
 * @param {boolean} showObjectExtras - Show helpers flag
 * @returns {Object} Lights state dan methods
 */

import { useState, useRef, useCallback } from 'react'

export function useCustomLights(sceneRef, showObjectExtras = true) {
  const [customLights, setCustomLights] = useState([])
  const [selectedLight, setSelectedLight] = useState(null)
  
  const customLightsRef = useRef([])
  const lightHelpersRef = useRef([])

  // Add custom light
  const addCustomLight = useCallback(
    (type = 'point') => {
      if (!sceneRef.current || !window.THREE_MODULE) return

      const THREE = window.THREE_MODULE
      const lightId = Date.now().toString()

      let light, helper
      const defaultConfig = {
        id: lightId,
        type: type,
        name: `${type.charAt(0).toUpperCase() + type.slice(1)} Light ${customLights.length + 1}`,
        color: '#ffffff',
        intensity: 1,
        position: { x: 0, y: 5, z: 5 },
        visible: true,
      }

      switch (type) {
        case 'point':
          light = new THREE.PointLight(defaultConfig.color, defaultConfig.intensity, 0, 2)
          light.position.set(
            defaultConfig.position.x,
            defaultConfig.position.y,
            defaultConfig.position.z
          )
          helper = new THREE.PointLightHelper(light, 0.5)
          defaultConfig.distance = 0
          defaultConfig.decay = 2
          break

        case 'spot':
          light = new THREE.SpotLight(defaultConfig.color, defaultConfig.intensity)
          light.position.set(
            defaultConfig.position.x,
            defaultConfig.position.y,
            defaultConfig.position.z
          )
          light.angle = Math.PI / 6
          light.penumbra = 0.1
          light.decay = 2
          light.distance = 0
          light.castShadow = true
          light.shadow.mapSize.width = 1024
          light.shadow.mapSize.height = 1024
          light.shadow.camera.near = 0.5
          light.shadow.camera.far = 50
          light.shadow.bias = -0.0001
          light.shadow.radius = 3
          helper = new THREE.SpotLightHelper(light)
          defaultConfig.target = { x: 0, y: 0, z: 0 }
          defaultConfig.angle = Math.PI / 6
          defaultConfig.penumbra = 0.1
          defaultConfig.distance = 0
          defaultConfig.decay = 2
          defaultConfig.castShadow = true
          defaultConfig.shadowRadius = 3
          break

        case 'directional':
          light = new THREE.DirectionalLight(defaultConfig.color, defaultConfig.intensity)
          light.position.set(
            defaultConfig.position.x,
            defaultConfig.position.y,
            defaultConfig.position.z
          )
          light.castShadow = true
          light.shadow.mapSize.width = 1024
          light.shadow.mapSize.height = 1024
          light.shadow.camera.near = 0.5
          light.shadow.camera.far = 50
          light.shadow.camera.left = -10
          light.shadow.camera.right = 10
          light.shadow.camera.top = 10
          light.shadow.camera.bottom = -10
          light.shadow.bias = -0.0001
          light.shadow.radius = 3
          helper = new THREE.DirectionalLightHelper(light, 1)
          defaultConfig.target = { x: 0, y: 0, z: 0 }
          defaultConfig.castShadow = true
          defaultConfig.shadowRadius = 3
          break

        case 'rect':
          light = new THREE.RectAreaLight(defaultConfig.color, defaultConfig.intensity, 4, 4)
          light.position.set(
            defaultConfig.position.x,
            defaultConfig.position.y,
            defaultConfig.position.z
          )
          light.lookAt(0, 0, 0)
          const rectHelper = new THREE.Mesh(
            new THREE.PlaneGeometry(4, 4),
            new THREE.MeshBasicMaterial({
              color: defaultConfig.color,
              wireframe: true,
              transparent: true,
              opacity: 0.3,
            })
          )
          rectHelper.position.copy(light.position)
          rectHelper.quaternion.copy(light.quaternion)
          helper = rectHelper
          defaultConfig.width = 4
          defaultConfig.height = 4
          break
      }

      light.userData.id = lightId
      light.userData.config = defaultConfig

      sceneRef.current.add(light)
      if (helper) {
        helper.visible = showObjectExtras
        sceneRef.current.add(helper)
        lightHelpersRef.current.push({ id: lightId, helper })
      }

      customLightsRef.current.push({ id: lightId, light, config: defaultConfig })
      setCustomLights([...customLightsRef.current.map((l) => l.config)])

      console.log(`Added ${type} light:`, defaultConfig)
    },
    [sceneRef, showObjectExtras, customLights.length]
  )

  // Update custom light
  const updateCustomLight = useCallback(
    (lightId, property, value) => {
      const lightData = customLightsRef.current.find((l) => l.id === lightId)
      if (!lightData || !window.THREE_MODULE) return

      const THREE = window.THREE_MODULE
      const { light, config } = lightData

      config[property] = value

      if (property === 'position') {
        light.position.set(value.x, value.y, value.z)
      } else if (property === 'target' && light.target) {
        light.target.position.set(value.x, value.y, value.z)
        light.target.updateMatrixWorld()
      } else if (property === 'color') {
        light.color.setStyle(value)
        if (config.type === 'rect') {
          const helperData = lightHelpersRef.current.find((h) => h.id === lightId)
          if (helperData?.helper.material) {
            helperData.helper.material.color.setStyle(value)
          }
        }
      } else if (property === 'intensity') {
        light.intensity = value
      } else if (property === 'visible') {
        light.visible = value
        const helperData = lightHelpersRef.current.find((h) => h.id === lightId)
        if (helperData) {
          helperData.helper.visible = value && showObjectExtras
        }
      } else if (property === 'castShadow' && light.castShadow !== undefined) {
        light.castShadow = value
      } else if (property === 'shadowRadius' && light.shadow?.radius !== undefined) {
        light.shadow.radius = value
      } else {
        light[property] = value
      }

      const helperData = lightHelpersRef.current.find((h) => h.id === lightId)
      if (helperData?.helper.update) {
        helperData.helper.update()
      }

      setCustomLights([...customLightsRef.current.map((l) => l.config)])
    },
    [showObjectExtras]
  )

  // Delete custom light
  const deleteCustomLight = useCallback(
    (lightId) => {
      const lightData = customLightsRef.current.find((l) => l.id === lightId)
      if (!lightData || !sceneRef.current) return

      sceneRef.current.remove(lightData.light)
      lightData.light.dispose?.()

      const helperData = lightHelpersRef.current.find((h) => h.id === lightId)
      if (helperData) {
        sceneRef.current.remove(helperData.helper)
        if (helperData.helper.geometry) helperData.helper.geometry.dispose()
        if (helperData.helper.material) helperData.helper.material.dispose()
      }

      customLightsRef.current = customLightsRef.current.filter((l) => l.id !== lightId)
      lightHelpersRef.current = lightHelpersRef.current.filter((h) => h.id !== lightId)
      setCustomLights([...customLightsRef.current.map((l) => l.config)])

      if (selectedLight?.id === lightId) {
        setSelectedLight(null)
      }

      console.log(`Deleted light: ${lightId}`)
    },
    [sceneRef, selectedLight]
  )

  return {
    customLights,
    setCustomLights,
    selectedLight,
    setSelectedLight,
    customLightsRef,
    lightHelpersRef,
    addCustomLight,
    updateCustomLight,
    deleteCustomLight,
  }
}

